<template>
  <!-- Title / sort row — classic DataTables-style: all sortable cols show an
       indicator; only the active sort column is highlighted. -->
  <tr>
    <th
      v-for="column in columns"
      :key="`title-${column.key}`"
      scope="col"
      class="v-data-table__th"
      :class="{
        'v-data-table__th--sortable': column.sortable !== false && !isSelectColumn(column),
        'v-data-table__th--sorted': sorted(column),
        'v-data-table__th--align-end': column.align === 'end',
        'v-data-table__th--align-center': column.align === 'center' || isSelectColumn(column),
      }"
      :style="headerStyle(column)"
      :tabindex="column.sortable !== false && !isSelectColumn(column) ? 0 : undefined"
      @click="onSort(column, $event)"
      @keydown.enter.prevent="onSort(column, $event)"
    >
      <div v-if="isSelectColumn(column)" class="v-data-table-header__content" @click.stop>
        <v-checkbox-btn
          v-if="typeof selectAll === 'function'"
          :model-value="allSelected"
          :indeterminate="someSelected && !allSelected"
          color="primary"
          @update:model-value="(v) => selectAll(!!v)"
        />
      </div>
      <div v-else class="v-data-table-header__content">
        <span>{{ column.title }}</span>
        <v-icon
          v-if="column.sortable !== false && !isSelectColumn(column)"
          class="v-data-table-header__sort-icon index-sort-icon"
          :class="{ 'index-sort-icon--active': sorted(column) }"
          size="small"
          :icon="sortIcon(column)"
        />
      </div>
    </th>
  </tr>

  <!-- Filter inputs aligned under each column -->
  <tr class="index-col-filter-row">
    <th
      v-for="column in columns"
      :key="`filter-${column.key}`"
      scope="col"
      class="v-data-table__th index-col-filter-row__cell"
      :style="headerStyle(column)"
    >
      <FlatpickrDateField
        v-if="isSearchable(column) && isDateColumn(column)"
        :model-value="modelValue[columnFieldName(column)] || ''"
        :placeholder="column.title"
        :aria-label="`Search ${column.title}`"
        :mode="dateFilterMode(column)"
        :date-format="dateFilterFormat(column)"
        field-class="index-col-filter-row__input index-col-filter-row__input--date"
        @update:model-value="onInput(columnFieldName(column), $event)"
        @enter="emit('apply')"
      />
      <v-text-field
        v-else-if="isSearchable(column)"
        :model-value="modelValue[columnFieldName(column)] || ''"
        type="text"
        :placeholder="column.title"
        :aria-label="`Search ${column.title}`"
        density="compact"
        hide-details
        variant="outlined"
        clearable
        single-line
        class="index-col-filter-row__input"
        @click.stop
        @mousedown.stop
        @update:model-value="onInput(columnFieldName(column), $event)"
        @keyup.enter.stop="emit('apply')"
        @click:clear.stop="clearOne(columnFieldName(column))"
      />
    </th>
  </tr>
</template>

<script setup>
import { computed, onBeforeUnmount } from 'vue'
import FlatpickrDateField from '@/shared/components/FlatpickrDateField.vue'

const props = defineProps({
  /** Table header columns from v-data-table #headers slot */
  columns: { type: Array, default: () => [] },
  toggleSort: { type: Function, default: null },
  isSorted: { type: Function, default: null },
  getSortIcon: { type: Function, default: null },
  /** Selection helpers from #headers when show-select is enabled */
  selectAll: { type: Function, default: null },
  allSelected: { type: Boolean, default: false },
  someSelected: { type: Boolean, default: false },
  /** Boot / index column meta (no_search, type, name) */
  metaColumns: { type: Array, default: () => [] },
  modelValue: { type: Object, default: () => ({}) },
  exclude: { type: Array, default: () => ['actions'] },
  debounceMs: { type: Number, default: 500 },
})

const emit = defineEmits(['update:modelValue', 'apply'])

let debounceTimer = null

const metaByName = computed(() => {
  const map = {}
  ;(props.metaColumns || []).forEach((c) => {
    if (c?.name) map[c.name] = c
  })
  return map
})

/**
 * Server / boot column name — may differ from header key (e.g. CMMS id → __row_id__).
 * Only string `value` is used. Function accessors (sort helpers) must not be used as
 * object keys: identical Function#toString across columns collapses all filters into one.
 */
function columnFieldName(column) {
  const value = column?.value
  if (typeof value === 'string' && value) return value
  return column?.key
}

function headerStyle(column) {
  const style = {}
  if (column.width != null) style.width = typeof column.width === 'number' ? `${column.width}px` : column.width
  if (column.minWidth != null) style.minWidth = typeof column.minWidth === 'number' ? `${column.minWidth}px` : column.minWidth
  if (column.maxWidth != null) style.maxWidth = typeof column.maxWidth === 'number' ? `${column.maxWidth}px` : column.maxWidth
  // Match Vuetify body-cell alignment when #headers is customized.
  if (isSelectColumn(column) || column.align === 'center') {
    style.textAlign = 'center'
  } else if (column.align === 'end') {
    style.textAlign = 'end'
  }
  return style
}

function isSelectColumn(column) {
  const key = column?.key
  return key === 'data-table-select' || key === '_select'
}

function isSearchable(column) {
  const name = columnFieldName(column)
  if (!name || props.exclude.includes(name)) return false
  if (String(column?.key || '').startsWith('data-table-')) return false
  const meta = metaByName.value[name]
  if (meta?.no_search) return false
  if (meta?.type === 'link') return false
  return true
}

function onSort(column, event) {
  if (isSelectColumn(column)) return
  // Vuetify defaults to sortable unless explicitly false; treat undefined as sortable.
  if (column?.sortable === false || typeof props.toggleSort !== 'function') return
  props.toggleSort(column, event)
}

function isDateColumn(column) {
  const meta = metaByName.value[columnFieldName(column)]
  return meta?.type === 'datetime' || meta?.type === 'date'
}

function dateFilterMode(_column) {
  // Classic addColumnSearch always uses Y-m-d (no time) for date and datetime columns.
  return 'date'
}

function dateFilterFormat(_column) {
  return 'Y-m-d'
}

function sorted(column) {
  return typeof props.isSorted === 'function' ? !!props.isSorted(column) : false
}

function sortIcon(column) {
  // Unsorted: dual arrows (classic DataTables "sortable" affordance)
  if (!sorted(column)) return 'mdi-swap-vertical'
  if (typeof props.getSortIcon === 'function') {
    return props.getSortIcon(column)
  }
  return 'mdi-arrow-up'
}

function setFilter(name, value) {
  if (typeof name !== 'string' || !name) return
  emit('update:modelValue', {
    ...props.modelValue,
    [name]: value ?? '',
  })
}

function onInput(name, value) {
  setFilter(name, value)
  if (!props.debounceMs) return
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => emit('apply'), props.debounceMs)
}

function clearOne(name) {
  clearTimeout(debounceTimer)
  setFilter(name, '')
  emit('apply')
}

onBeforeUnmount(() => {
  clearTimeout(debounceTimer)
})
</script>

<style scoped>
.index-col-filter-row__cell {
  padding: 4px 8px !important;
  vertical-align: middle !important;
  font-weight: normal !important;
  background: rgba(var(--v-theme-surface), 1);
  border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  min-width: 110px;
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
}

.index-col-filter-row__input {
  width: 100%;
  min-width: 0;
}

.index-col-filter-row__input :deep(.v-field) {
  border-radius: 6px;
  font-weight: 400;
}

.index-col-filter-row__input :deep(.v-field__input) {
  min-height: 32px;
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 0.8125rem;
}

.index-col-filter-row__input :deep(input) {
  font-size: 0.8125rem;
}

.index-col-filter-row__input--date :deep(input) {
  min-width: 0;
  cursor: pointer;
}

/* Classic DataTables: always show sort affordance; highlight only active column */
.index-sort-icon {
  opacity: 0.38 !important;
  margin-inline-start: 2px;
  transition: opacity 0.15s ease, color 0.15s ease;
}

.v-data-table__th--sortable:hover .index-sort-icon:not(.index-sort-icon--active) {
  opacity: 0.6 !important;
}

.index-sort-icon--active {
  opacity: 1 !important;
  color: rgb(var(--v-theme-primary));
}

.v-data-table__th:has(.v-checkbox-btn) .v-data-table-header__content {
  justify-content: center;
}

.v-data-table__th--align-end .v-data-table-header__content {
  width: 100%;
  justify-content: flex-end;
}

.v-data-table__th--align-center .v-data-table-header__content {
  width: 100%;
  justify-content: center;
}
</style>

<!-- flatpickr calendar is teleported to body; keep it above table chrome -->
<style>
.flatpickr-calendar {
  z-index: 3000 !important;
}
</style>
